import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { isPromoTvActivatedSelector } from "@redux/featureFlags/selector";
import { roomCountrySelector } from "@redux/room/selectors";

import { ReactComponent as BamLogo } from "assets/logo-bam.svg";

export type PromoImage = "first" | "middle" | "end";

interface BamPromoImagesProps {
	promoImage: PromoImage;
}

export const BamPromoImage = ({ promoImage }: BamPromoImagesProps) => {
	const roomCountry = useSelector(roomCountrySelector);
	const isPromoImageActivated = useSelector(isPromoTvActivatedSelector);

	const [imageUrls, setImageUrls] = useState<string[]>([]);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [nextIndex, setNextIndex] = useState(0);
	const [isTransitioning, setIsTransitioning] = useState(false);

	useEffect(() => {
		if (!roomCountry || !isPromoImageActivated) {
			return;
		}

		const baseUrl = `${process.env.REACT_APP_ASSETS_BASE_URL}/promo/${roomCountry}`;
		const loadImages = async () => {
			const urls: string[] = [];
			for (let i = 1; i <= 10; i++) {
				const url = `${baseUrl}/${promoImage}_${i}.jpg`;
				try {
					const res = await fetch(url, { method: "HEAD" });
					if (res.ok) {
						urls.push(url);
					} else {
						break;
					}
				} catch {
					break;
				}
			}

			if (urls.length === 0) {
				urls.push(`${baseUrl}/${promoImage}.jpg`);
			}

			setImageUrls(urls);
		};

		loadImages();
	}, [promoImage, roomCountry, isPromoImageActivated]);

	useEffect(() => {
		if (imageUrls.length <= 1) {
			return;
		}

		const interval = setInterval(() => {
			const next = (currentIndex + 1) % imageUrls.length;
			setNextIndex(next);
			setIsTransitioning(true);

			setTimeout(() => {
				setCurrentIndex(next);
				setIsTransitioning(false);
			}, 500); // transition duration
		}, 10000); // change image every 10 seconds

		return () => clearInterval(interval);
	}, [currentIndex, imageUrls]);

	if (!roomCountry || !isPromoImageActivated) {
		return <BamLogo width="40%" />;
	}

	const currentImage = imageUrls[currentIndex];
	const nextImage = imageUrls[nextIndex];

	return (
		<div style={{ position: "relative", width: "100%", height: "100%" }}>
			{/* Current image */}
			<img
				src={currentImage}
				alt="promo-current"
				style={{
					position: "absolute",
					width: "100%",
					height: "100%",
					objectFit: "cover",
					opacity: isTransitioning ? 0 : 1,
					transition: "opacity 0.5s ease-in-out",
					zIndex: 1,
				}}
			/>
			{/* Next image */}
			{isTransitioning && (
				<img
					src={nextImage}
					alt="promo-next"
					style={{
						position: "absolute",
						width: "100%",
						height: "100%",
						objectFit: "cover",
						opacity: isTransitioning ? 0 : 1,
						transition: "opacity 0.5s ease-in-out",
						zIndex: 0,
					}}
				/>
			)}
		</div>
	);
};
